import { computed, inject } from "vue";
import { useStore } from "vuex";
import {
  ANNOTATION_CONTEXT_MODE,
  ANNOTATION_CONTEXT_TYPE,
  NAMESPACE,
} from "@modules/annotation/service/annotation.constant";
import type { AnnotationContext } from "@modules/annotation/types.ts";

interface ExistingAnnotationPermissionOptions {
  canEdit: boolean;
  isTeacherView: boolean;
  viewOnly?: boolean;
}

interface AnnotationRecord {
  createdBy?: unknown;
  teacherEditable?: unknown;
  outdated?: unknown;
}

export function useAnnotationPermissions(providedContext: AnnotationContext | null = null) {
  const store = useStore();
  const annotationContext =
    providedContext ?? inject<AnnotationContext | null>("annotationContext", null);

  const userId = computed(() => store.getters[`${NAMESPACE.AUTH}/userId`]);
  const editorResourceDomain = computed(
    () => store.getters[`${NAMESPACE.EDITOR_LESSON}/resourceDomain`],
  );
  const isEditorAdminPage = computed(() => store.getters[`${NAMESPACE.EDITOR_LESSON}/isAdminPage`]);

  const isEditorContext = computed(
    () => annotationContext?.contextType === ANNOTATION_CONTEXT_TYPE.EDITOR,
  );

  const isEditorEditMode = computed(
    () => isEditorContext.value && annotationContext?.mode === ANNOTATION_CONTEXT_MODE.EDIT,
  );

  const isEditorAnnotationCreationAllowed = computed(() => {
    if (!annotationContext) {
      return false;
    }

    if (annotationContext?.contextType !== ANNOTATION_CONTEXT_TYPE.EDITOR) {
      return true;
    }

    // MOE library resources only show annotations when reached via the Manage module
    // (routed with the admin param) - regular MOE library browse/preview must not.
    return (
      editorResourceDomain.value === "class-group" ||
      (editorResourceDomain.value === "moe-library" && isEditorAdminPage.value)
    );
  });

  const canAccessAnnotations = computed(() => isEditorAnnotationCreationAllowed.value);

  const canCreateAnnotations = computed(
    () => !isEditorEditMode.value && isEditorAnnotationCreationAllowed.value,
  );

  const isOwned = (annotation: unknown): boolean => {
    return (
      !!annotation &&
      typeof annotation === "object" &&
      "createdBy" in annotation &&
      (annotation as Record<string, unknown>).createdBy === parseInt(userId.value)
    );
  };

  const isSystemCreated = (annotation: unknown): boolean => {
    return (
      !!annotation &&
      typeof annotation === "object" &&
      "createdBy" in annotation &&
      (annotation as Record<string, unknown>).createdBy === 1
    );
  };

  const haveEditAccess = (
    annotation: unknown,
    options: ExistingAnnotationPermissionOptions,
  ): boolean => {
    if (isEditorContext.value) {
      return isOwned(annotation);
    }

    const annotationRecord = (annotation ?? {}) as AnnotationRecord;
    return options.isTeacherView && !!annotationRecord.teacherEditable && options.canEdit
      ? true
      : isOwned(annotation);
  };

  const canEditAnnotation = (
    annotation: unknown,
    options: ExistingAnnotationPermissionOptions,
  ): boolean => {
    const annotationRecord = (annotation ?? {}) as AnnotationRecord;

    return haveEditAccess(annotation, options) && !annotationRecord.outdated && !options.viewOnly;
  };

  const canDeleteAnnotation = (
    annotation: unknown,
    options: ExistingAnnotationPermissionOptions,
  ): boolean => {
    if (isEditorContext.value) {
      return isOwned(annotation);
    }

    if (options.isTeacherView && isSystemCreated(annotation) && options.canEdit) {
      return !options.viewOnly;
    }

    return isOwned(annotation);
  };

  const isCommentTogglingSupported = computed(() => !isEditorContext.value);

  const canToggleComments = (
    annotation: unknown,
    options: Pick<ExistingAnnotationPermissionOptions, "canEdit" | "isTeacherView">,
  ): boolean => {
    if (!isCommentTogglingSupported.value) {
      return false;
    }

    if (options.isTeacherView && options.canEdit) {
      return true;
    }

    return isOwned(annotation);
  };

  return {
    isOwned,
    isEditorContext,
    canAccessAnnotations,
    canCreateAnnotations,
    haveEditAccess,
    canEditAnnotation,
    canDeleteAnnotation,
    canToggleComments,
    isCommentTogglingSupported,
  };
}
